import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'

export interface Notification {
  id: string
  userId: string
  type: 'comment' | 'follow' | 'like' | 'new_review' | 'system_alert' | 'announcement'
  title: string
  message: string
  isRead: boolean
  metadata?: Record<string, unknown> | null
  createdAt: string
  updatedAt: string
}

export interface PlatformMessage {
  key: string
  title: string
  message: string
  severity: 'info' | 'warning'
}

export interface NotificationDigestResult {
  sessionReminderSent: boolean
  wishlistAlertsSent: number
}

export interface NotificationPreferences {
  emailNotifications: boolean
  pushNotifications: boolean
  notifyComments: boolean
  notifyFollows: boolean
  notifyLikes: boolean
  notifyNewReviews: boolean
  notifySystemAlerts: boolean
  notifyAnnouncements: boolean
}

interface ApiResponse<T> {
  success: boolean
  message?: string
  data: T
}

interface NotificationsResponse {
  notifications: Notification[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
  unreadCount: number
}

export const notificationApi = createApi({
  reducerPath: 'notificationApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/notifications',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['Notification', 'UnreadCount', 'NotificationPreferences'],
  endpoints: (builder) => ({
    getNotifications: builder.query<NotificationsResponse, { page?: number; limit?: number; unreadOnly?: boolean }>({
      query: ({ page = 1, limit = 20, unreadOnly = false } = {}) => ({
        url: '/',
        params: { page, limit, unreadOnly },
      }),
      transformResponse: (response: ApiResponse<NotificationsResponse>) => response.data,
      providesTags: ['Notification', 'UnreadCount'],
    }),

    getUnreadCount: builder.query<number, void>({
      query: () => '/unread-count',
      transformResponse: (response: ApiResponse<{ count: number }>) => response.data.count,
      providesTags: ['UnreadCount'],
    }),

    markAsRead: builder.mutation<Notification, string>({
      query: (id) => ({
        url: `/${id}/read`,
        method: 'PUT',
      }),
      transformResponse: (response: ApiResponse<Notification>) => response.data,
      invalidatesTags: ['Notification', 'UnreadCount'],
    }),

    markAllAsRead: builder.mutation<{ updatedCount: number }, void>({
      query: () => ({
        url: '/mark-all-read',
        method: 'PUT',
      }),
      transformResponse: (response: ApiResponse<{ updatedCount: number }>) => response.data,
      invalidatesTags: ['Notification', 'UnreadCount'],
    }),

    deleteNotification: builder.mutation<void, string>({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Notification', 'UnreadCount'],
    }),

    getNotificationPreferences: builder.query<NotificationPreferences, void>({
      query: () => '/preferences',
      transformResponse: (response: ApiResponse<NotificationPreferences>) => response.data,
      providesTags: ['NotificationPreferences'],
    }),

    updateNotificationPreferences: builder.mutation<NotificationPreferences, Partial<NotificationPreferences>>({
      query: (preferences) => ({
        url: '/preferences',
        method: 'PUT',
        body: preferences,
      }),
      transformResponse: (response: ApiResponse<NotificationPreferences>) => response.data,
      invalidatesTags: ['NotificationPreferences'],
    }),

    getPlatformMessages: builder.query<PlatformMessage[], void>({
      query: () => '/platform-messages',
      transformResponse: (response: ApiResponse<PlatformMessage[]>) => response.data,
    }),

    runDigestChecks: builder.mutation<NotificationDigestResult, void>({
      query: () => ({
        url: '/digest-check',
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<NotificationDigestResult>) => response.data,
      invalidatesTags: ['Notification', 'UnreadCount'],
    }),
  }),
})

export const {
  useGetNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
  useDeleteNotificationMutation,
  useGetNotificationPreferencesQuery,
  useUpdateNotificationPreferencesMutation,
  useGetPlatformMessagesQuery,
  useRunDigestChecksMutation,
} = notificationApi
